/* Hydronate homepage interactivity. One file, no dependencies, safe to run before or after DOM ready.
   Every behaviour is scoped to a section root so the theme editor can add/remove/reorder sections
   and re-initialising is always idempotent. */
(function () {
  'use strict';

  var COBALT = '#1E36C8';
  var INK = '#16258F';
  var LINEN = '#DCD1BF';
  var LINEN_BG = '#FBF6EE';
  var SAND_RING = '#C9BCA4';

  function each(list, fn) { Array.prototype.forEach.call(list || [], fn); }
  function qs(root, sel) { return root.querySelector(sel); }
  function qsa(root, sel) { return root.querySelectorAll(sel); }

  /* ---------------------------------------------------------------- nav */
  function initNav(root) {
    var burger = qs(root, '[data-hn-burger]');
    var menu = qs(root, '[data-hn-menu]');
    if (!burger || !menu) return;

    var top = qs(root, '[data-hn-burger-top]');
    var mid = qs(root, '[data-hn-burger-mid]');
    var bot = qs(root, '[data-hn-burger-bot]');
    var label = qs(root, '[data-hn-menu-label]');
    var openLabel = (label && label.textContent.trim()) || 'Menu';

    var sticky = initNavSticky(root, menu);

    function paint(open) {
      menu.style.display = open ? 'block' : 'none';
      if (top) top.style.transform = open ? 'translateY(5.8px) rotate(45deg)' : 'none';
      if (mid) mid.style.opacity = open ? '0' : '1';
      if (bot) bot.style.transform = open ? 'translateY(-5.8px) rotate(-45deg)' : 'none';
      if (label) label.textContent = open ? 'Close' : openLabel;
      burger.setAttribute('aria-expanded', open ? 'true' : 'false');
      /* Once the bar is fixed the panel hangs off the viewport instead of the
         document, so a tall menu on a short phone would put the bottom links
         somewhere unreachable. Cap the panel and let IT scroll — not the bar,
         which has to stay pinned so "Close" is always in reach. */
      if (sticky) sticky.onMenu(open);
    }

    burger.setAttribute('role', 'button');
    burger.setAttribute('tabindex', '0');
    paint(false);

    burger.addEventListener('click', function () {
      paint(menu.style.display === 'none');
    });
    burger.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); paint(menu.style.display === 'none'); }
    });
    each(qsa(root, '[data-hn-menu-close]'), function (el) {
      el.addEventListener('click', function () { paint(false); });
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && menu.style.display !== 'none') paint(false);
    });
  }

  /* --------------------------------------------------------- sticky nav */
  /* The bar follows the shopper down the page so the cart button (and its
     item count) is never more than a thumb away. It gets out of the way while
     they are actively scrolling DOWN, and comes back the moment they scroll up
     or simply stop — which is the behaviour Brandon asked for, and is more
     forgiving than reveal-on-scroll-up alone: you never have to "earn" the bar
     back with an upward flick.

     Why position:fixed and not position:sticky — sticky resolves against the
     nearest scrolling ancestor, and every HN section is wrapped in its own
     #shopify-section div. `top:0` inside a wrapper that is only as tall as the
     bar itself sticks for exactly zero pixels. Going fixed means taking the bar
     out of flow, so we hand its former height to the wrapper as a placeholder. */
  function initNavSticky(root, menu) {
    if (root.getAttribute('data-hn-sticky') === 'off') return null;
    var bar = qs(root, '[data-m="nav"]');
    var holder = root.parentElement;
    if (!bar || !holder) return null;

    /* Someone who has asked their OS for less motion should not get a bar that
       slides in and out of view on every scroll gesture. They still get a
       pinned bar — it just never leaves. */
    var mq = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');
    var mayHide = !(mq && mq.matches);
    var idleReveal = root.getAttribute('data-hn-sticky-idle') !== 'off';

    var h = 0;
    function measure() {
      var next = bar.offsetHeight;
      if (!next || next === h) return;
      h = next;
      holder.style.height = h + 'px';
      /* Every in-page jump (#offer, #faq, the footer links) would otherwise
         land with its heading tucked under the bar. One property fixes all of
         them, including the browser's own restore-scroll on reload. */
      document.documentElement.style.scrollPaddingTop = (h + 10) + 'px';
      /* Published so anything else that pins itself can clear the bar. The
         offer gallery is position:sticky top:24px — without this it parks 24px
         from the VIEWPORT top, which is underneath the bar. */
      document.documentElement.style.setProperty('--hn-navh', h + 'px');
      if (menu) menu.style.maxHeight = 'calc(100vh - ' + h + 'px)';
    }

    measure();
    root.style.position = 'fixed';
    root.style.top = '0';
    root.style.left = '0';
    root.style.right = '0';
    root.style.zIndex = '400';
    root.style.willChange = 'transform';
    if (mayHide) root.style.transition = 'transform .26s cubic-bezier(.4,0,.2,1)';

    /* offsetHeight is measured before Manrope has necessarily swapped in, and
       the bar's padding drops from 18px to 15px under 1180px. Re-measure on
       both rather than trusting the first read. */
    if (window.ResizeObserver) new ResizeObserver(measure).observe(bar);
    else window.addEventListener('resize', measure);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(measure);

    /* A `position: fixed` element resolves against the viewport ONLY if no
       ancestor has been promoted to a containing block — a transform, filter,
       perspective or will-change all do that. theme.css keeps an identity
       `transform: translateX(0)` on #PageContainer for its old off-canvas
       drawer, which silently re-anchored this bar to a box that scrolls.
       hn-home.css clears that one declaratively; this catches anything else (an
       app wrapper, a future theme.css edit) and is deliberately timid: it only
       removes IDENTITY transforms, where removal is a visual no-op by
       definition. Anything else just gets reported, because guessing at a real
       transform would trade a stuck header for a broken layout. */
    var IDENTITY = { 'matrix(1, 0, 0, 1, 0, 0)': 1, 'matrix3d(1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1)': 1 };
    var healed = false;
    function heal() {
      if (healed) return;
      /* getComputedStyle would report `fixed` either way, so the only reliable
         tell is the measured box drifting from the viewport top. */
      if (Math.abs(bar.getBoundingClientRect().top) < 3) { healed = true; return; }
      healed = true;
      var el = root.parentElement, stuck = [];
      while (el && el !== document.documentElement) {
        var t = getComputedStyle(el).transform;
        if (t !== 'none') {
          if (IDENTITY[t]) el.style.setProperty('transform', 'none', 'important');
          else stuck.push((el.id || el.tagName) + ' transform:' + t);
        }
        el = el.parentElement;
      }
      if (stuck.length && window.console) {
        console.warn('[hn-nav] pinned bar is trapped in a transformed ancestor; ' +
          'add a `transform: none` override for these in hn-home.css:', stuck);
      }
    }

    var hidden = false;
    function show() {
      if (!hidden) return;
      hidden = false;
      root.style.transform = 'translateY(0)';
      bar.setAttribute('aria-hidden', 'false');
    }
    function hide() {
      if (hidden || !mayHide) return;
      hidden = true;
      root.style.transform = 'translateY(-100%)';
      bar.setAttribute('aria-hidden', 'true');
    }

    function pinned() {
      /* Mid-interaction is the worst possible moment to yank the bar away: the
         open menu hangs off it, and the cart drawer's own scroll can bubble. */
      if (menu && menu.style.display !== 'none') return true;
      return document.documentElement.classList.contains('hncd-locked');
    }

    var lastY = window.pageYOffset || 0;
    var ticking = false;
    var idleTimer = null;

    function frame() {
      ticking = false;
      var y = window.pageYOffset || document.documentElement.scrollTop || 0;
      var dy = y - lastY;
      lastY = y;

      /* Flat against the page at rest, lifted once it is genuinely floating
         over content — otherwise the shadow reads as a rendering artefact. */
      bar.style.boxShadow = y > 4 ? '0 8px 24px rgba(22,37,143,.10)' : 'none';

      /* Checked on the first real scroll, when a mis-anchored bar has actually
         had a chance to drift. At y=0 the broken and working cases measure
         identically, so testing any earlier proves nothing. */
      if (y > 60) heal();

      if (pinned()) { show(); return; }
      /* Within the first screenful there is nothing to reclaim by hiding, and
         iOS rubber-banding around y=0 would flicker the bar. */
      if (y <= h + 40) { show(); return; }

      /* The 5px deadband ignores trackpad jitter and the 1px nudges that fire
         when a lazy image finally settles into its box. */
      if (dy > 5) hide();
      else if (dy < -5) show();

      if (idleReveal) {
        clearTimeout(idleTimer);
        /* Long enough that a pause mid-flick does not pop the bar in, short
           enough that stopping to read brings the cart straight back. */
        idleTimer = setTimeout(show, 850);
      }
    }

    window.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(frame);
    }, { passive: true });

    return {
      onMenu: function (open) {
        if (open) { show(); if (menu) menu.style.overflowY = 'auto'; }
      }
    };
  }

  /* ----------------------------------------------------------- benefits */
  function initBenefits(root) {
    var pills = qsa(root, '[data-hn-pill]');
    if (!pills.length) return;

    function select(idx) {
      each(pills, function (pill) {
        var on = pill.getAttribute('data-hn-pill') === String(idx);
        pill.setAttribute('data-on', on ? '1' : '0');
        pill.style.background = on ? COBALT : LINEN_BG;
        pill.style.borderColor = on ? COBALT : '#DFD3BE';
        pill.style.boxShadow = on ? '0 12px 28px rgba(22,37,143,.24)' : '0 1px 0 rgba(22,37,143,.04)';
        var chip = qs(pill, '[data-hn-chip]');
        if (chip) chip.style.background = on ? LINEN_BG : '#EFE6D6';
        var lbl = qs(pill, '[data-hn-pill-label]');
        if (lbl) lbl.style.color = on ? '#FFFFFF' : INK;
        each(qsa(pill, '[data-hn-pill-sub]'), function (sub) {
          sub.style.color = on ? '#C3CEF6' : '#574C39';
        });
      });
      ['plate', 'panel', 'research'].forEach(function (kind) {
        each(qsa(root, '[data-hn-' + kind + ']'), function (el) {
          el.style.display = el.getAttribute('data-hn-' + kind) === String(idx) ? 'block' : 'none';
        });
      });
    }

    each(pills, function (pill) {
      pill.addEventListener('click', function () {
        select(pill.getAttribute('data-hn-pill'));
      });
    });
  }

  /* -------------------------------------------------------------- offer */
  function initOffer(root) {
    var tiers = qsa(root, '[data-hn-tier]');
    var flavorTiles = qsa(root, '[data-hn-flavor]');
    var priceOut = qs(root, '[data-hn-atc-price]');
    var doseOut = qs(root, '[data-hn-dose-line]');
    var rebillOut = qs(root, '[data-hn-rebill]');
    var inputId = qs(root, '[data-hn-input-id]');
    var inputPlan = qs(root, '[data-hn-input-plan]');
    var form = qs(root, '[data-hn-add-form]');

    var tierIdx = 0;
    var flavorIdx = 0;
    var galIdx = 0;
    each(tiers, function (t, i) { if (t.getAttribute('data-on') === '1') tierIdx = i; });
    // A sold-out flavour is rendered but not buyable, so it must never become the
    // selected one -- resolveVariant() would hand the form a variant Shopify denies.
    function flavorSold(t) { return !!t && t.getAttribute('data-hn-flavor-soldout') === '1'; }
    each(flavorTiles, function (f, i) { if (f.getAttribute('data-on') === '1' && !flavorSold(f)) flavorIdx = i; });
    if (flavorSold(flavorTiles[flavorIdx])) {
      for (var fi = 0; fi < flavorTiles.length; fi++) {
        if (!flavorSold(flavorTiles[fi])) { flavorIdx = fi; break; }
      }
    }

    function resolveVariant() {
      var tier = tiers[tierIdx];
      var flavor = flavorTiles[flavorIdx];
      var id = flavor && flavor.getAttribute('data-variant-' + (tierIdx + 1));
      if (!id && tier) id = tier.getAttribute('data-variant');
      return id || '';
    }

    /* ---- Light Labs testing widget sync ---------------------------------
       The widget reads ONLY window.ShopifyAnalytics.meta.selectedVariantId, which
       does not exist on the homepage or the collection page, so we drive it with
       resolveVariant() -- the exact flavour x tier the customer has selected.
       Entirely inert when the section's Light Labs setting is off, because llEl
       is then null. */
    var llEl = qs(root, '[data-hnll] lightlabs-widget');

    function syncLightLabs() {
      if (!llEl) return true;
      var ll = window.lightlabs;
      if (!ll || typeof ll.setExternalId !== 'function') return false;
      var id = resolveVariant();
      if (id && ll.externalId !== id) ll.setExternalId(id);
      return true;
    }

    if (llEl) {
      // The Light Labs bundle is async, so window.lightlabs may not exist yet.
      (function waitForLL(tries) {
        if (syncLightLabs() || tries <= 0) return;
        setTimeout(function () { waitForLL(tries - 1); }, 250);
      })(60);
    }

    function paintTiers() {
      each(tiers, function (tile, i) {
        var on = i === tierIdx;
        tile.setAttribute('data-on', on ? '1' : '0');
        tile.style.borderColor = on ? COBALT : LINEN;
        tile.style.background = on ? '#E9EDFB' : LINEN_BG;
        var ring = qs(tile, '[data-hn-tier-ring]');
        if (ring) ring.style.borderColor = on ? COBALT : SAND_RING;
        var dot = qs(tile, '[data-hn-tier-dot]');
        if (dot) dot.style.background = on ? COBALT : 'transparent';
      });
      var tier = tiers[tierIdx];
      if (!tier) return;
      if (priceOut) priceOut.textContent = tier.getAttribute('data-price') || '';
      if (doseOut) doseOut.textContent = tier.getAttribute('data-dose-line') || '';
      if (rebillOut) rebillOut.textContent = tier.getAttribute('data-rebill') || '';
      if (inputPlan) inputPlan.value = tier.getAttribute('data-selling-plan') || '';
      if (inputId) inputId.value = resolveVariant();
      syncLightLabs();
    }

    function paintFlavors() {
      each(flavorTiles, function (tile, i) {
        var on = i === flavorIdx && !flavorSold(tile);
        tile.setAttribute('data-on', on ? '1' : '0');
        var accent = tile.getAttribute('data-accent') || COBALT;
        var tint = tile.getAttribute('data-tint') || 'rgba(226,232,250,0.6)';
        tile.style.borderColor = on ? accent : LINEN;
        tile.style.background = on ? tint : LINEN_BG;
        var ring = qs(tile, '[data-hn-flavor-ring]');
        if (ring) ring.style.borderColor = on ? accent : SAND_RING;
        var dot = qs(tile, '[data-hn-flavor-dot]');
        if (dot) dot.style.background = on ? accent : 'transparent';
      });
      each(qsa(root, '[data-hn-gal-flavor]'), function (img) {
        img.style.display = img.getAttribute('data-hn-gal-flavor') === String(flavorIdx) ? 'block' : 'none';
      });
      each(qsa(root, '[data-hn-thumb-flavor]'), function (img) {
        img.style.display = img.getAttribute('data-hn-thumb-flavor') === String(flavorIdx) ? 'block' : 'none';
      });
      if (inputId) inputId.value = resolveVariant();
      syncLightLabs();
    }

    function selectGallery(idx) {
      // Track the shown slide so the swipe handler has something to step from.
      // Was stateless before; every caller passed an absolute index.
      galIdx = Number(idx) || 0;
      each(qsa(root, '[data-hn-gal-main]'), function (img) {
        var slot = img.getAttribute('data-hn-gal-main');
        if (slot !== String(idx)) { img.style.display = 'none'; return; }
        // Slot 0 holds one image per flavor; only the active flavor's copy shows.
        var fl = img.getAttribute('data-hn-gal-flavor');
        img.style.display = (fl === null || fl === String(flavorIdx)) ? 'block' : 'none';
      });
      each(qsa(root, '[data-hn-gal-thumb]'), function (thumb) {
        var on = thumb.getAttribute('data-hn-gal-thumb') === String(idx);
        thumb.style.border = on ? '1.5px solid ' + COBALT : '1px solid ' + LINEN;
      });
    }

    each(tiers, function (tile, i) {
      tile.addEventListener('click', function () { tierIdx = i; paintTiers(); });
    });
    each(flavorTiles, function (tile, i) {
      tile.addEventListener('click', function () {
        if (flavorSold(tile)) return;   // sold-out tiles are inert
        flavorIdx = i;
        paintFlavors();
        selectGallery(0);
      });
    });
    each(qsa(root, '[data-hn-gal-thumb]'), function (thumb) {
      thumb.addEventListener('click', function () {
        selectGallery(thumb.getAttribute('data-hn-gal-thumb'));
      });
    });

    /* ---- swipe the main image -------------------------------------------
       The gallery is a STACK of absolutely-positioned <img> with exactly one
       set to display:block -- not a horizontal scroller. So there was no
       native overflow to swipe and no slider library behind it: the ONLY way
       to change slide was tapping a thumbnail, which is what customers hit on
       mobile.

       We measure the gesture and step on release. Deliberately NOT doing a
       drag-follow transform: that would mean rewriting the stack into a
       translated track, which is a much larger change to a section that also
       renders on the homepage and carries the live variant wiring.

       We never call preventDefault. There is no horizontal overflow to
       suppress, and staying passive means a diagonal drag keeps scrolling the
       page normally instead of feeling stuck. Tapping thumbnails still works
       unchanged, so this is an enhancement and not a new pointer-gesture
       dependency (WCAG 2.5.1). */
    var galFirst = qs(root, '[data-hn-gal-main]');
    var galStack = galFirst ? galFirst.parentNode : null;
    var galCount = qsa(root, '[data-hn-gal-thumb]').length;
    if (galStack && galCount > 1) {
      var swX = 0, swY = 0, swOn = false;
      galStack.addEventListener('touchstart', function (e) {
        // Ignore pinch/multi-touch outright; a two-finger zoom must not paginate.
        if (!e.touches || e.touches.length !== 1) { swOn = false; return; }
        swOn = true;
        swX = e.touches[0].clientX;
        swY = e.touches[0].clientY;
      }, { passive: true });
      galStack.addEventListener('touchend', function (e) {
        if (!swOn) return;
        swOn = false;
        var t = e.changedTouches && e.changedTouches[0];
        if (!t) return;
        var dx = t.clientX - swX;
        var dy = t.clientY - swY;
        // Horizontal has to clearly dominate. Without the dy test a vertical
        // flick that drifts sideways would silently change the image while the
        // customer believes they are just scrolling the page.
        if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
        var next = galIdx + (dx < 0 ? 1 : -1);
        // Clamp rather than wrap: looping back to the first image from the last
        // reads as a glitch when there is no visible slide indicator.
        if (next < 0 || next > galCount - 1) return;
        selectGallery(next);
      }, { passive: true });
    }

    /* ---- tier deep links -------------------------------------------------
       A link can preselect a tier by pointing at one of the alias anchors the
       offer section renders (#offer-3day / #offer-2day / #offer-1day). We resolve
       the id to an ELEMENT and read the key off it rather than parsing the hash
       string, so renaming the offer anchor in the editor cannot break this.
       Scrolling is left entirely to the browser's native anchor jump — we never
       preventDefault, so a JS failure degrades to today's plain scroll. */
    function selectTierByKey(key) {
      if (!key) return false;
      var found = -1;
      each(tiers, function (t, i) {
        if (t.getAttribute('data-hn-tier-key') === key) found = i;
      });
      if (found < 0 || found === tierIdx) return false;
      tierIdx = found;
      paintTiers();
      return true;
    }

    function applyTierId(rawId) {
      if (!rawId) return false;
      var id = rawId;
      try { id = decodeURIComponent(rawId); } catch (e) { /* keep raw */ }
      var el = document.getElementById(id) || document.getElementById(rawId);
      return el ? selectTierByKey(el.getAttribute('data-hn-tier-target')) : false;
    }

    window.addEventListener('hashchange', function () {
      applyTierId((location.hash || '').slice(1));
    });
    /* Clicking the SAME tier link twice fires no hashchange, so a shopper who
       clicked "Trial · 1/day", then hand-picked another tile, then clicked
       "Trial · 1/day" again would keep the hand-picked tile. Catch the click too.
       Links pointing at another page resolve to no local element and fall
       through untouched, so cross-page navigation is unaffected. */
    document.addEventListener('click', function (e) {
      var a = e.target && e.target.closest ? e.target.closest('a[href*="#"]') : null;
      if (!a) return;
      var href = a.getAttribute('href') || '';
      var hi = href.indexOf('#');
      if (hi < 0) return;
      applyTierId(href.slice(hi + 1));
    });

    initDisclosures(root);
    paintTiers();
    paintFlavors();
    // After the first paint: a hash already present at load (arriving from
    // another page) wins over the block's "Selected by default".
    applyTierId((location.hash || '').slice(1));
    if (form) initAddToCart(root, form, inputId, inputPlan);
  }

  /* ------------------------------------------------------- disclosures */
  function initDisclosures(root) {
    var buttons = qsa(root, '[data-hn-disclosure-btn]');
    if (!buttons.length) return;

    function paint(openKey) {
      each(qsa(root, '[data-hn-disclosure]'), function (panel) {
        panel.style.display = panel.getAttribute('data-hn-disclosure') === openKey ? 'block' : 'none';
      });
      each(buttons, function (btn) {
        var on = btn.getAttribute('data-hn-disclosure-btn') === openKey;
        btn.style.background = on ? COBALT : 'rgba(226,232,250,0.55)';
        btn.style.color = on ? '#F6F0E7' : COBALT;
        btn.style.borderColor = COBALT;
        btn.style.boxShadow = on ? '0 6px 16px rgba(22,37,143,0.22)' : '0 1px 3px rgba(22,37,143,0.08)';
        btn.setAttribute('aria-expanded', on ? 'true' : 'false');
        var caret = qs(btn, '[data-hn-caret]');
        if (caret) caret.style.transform = on ? 'rotate(180deg)' : 'rotate(0deg)';
      });
    }

    each(buttons, function (btn) {
      btn.setAttribute('role', 'button');
      btn.setAttribute('tabindex', '0');
      var key = btn.getAttribute('data-hn-disclosure-btn');
      function toggle() {
        var panel = qs(root, '[data-hn-disclosure="' + key + '"]');
        paint(panel && panel.style.display === 'block' ? null : key);
      }
      btn.addEventListener('click', toggle);
      btn.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); }
      });
    });
    each(qsa(root, '[data-hn-disclosure-close]'), function (el) {
      el.addEventListener('click', function () { paint(null); });
    });
    paint(null);
  }

  /* ---------------------------------------------------------- add to cart */
  function initAddToCart(root, form, inputId, inputPlan) {
    var fallback = root.getAttribute('data-fallback-url');
    var useAjax = root.getAttribute('data-ajax') === '1';

    form.addEventListener('submit', function (e) {
      var variant = inputId ? inputId.value.trim() : '';

      // No variant configured yet: never post an empty add — send them somewhere real instead.
      if (!variant) {
        e.preventDefault();
        if (fallback) window.location.href = fallback;
        return;
      }
      // Shopify rejects selling_plan="" on /cart/add, and a disabled input is not submitted.
      if (inputPlan) inputPlan.disabled = !inputPlan.value.trim();

      if (!useAjax) return;

      e.preventDefault();
      var plan = inputPlan ? inputPlan.value.trim() : '';
      var button = qs(form, 'button[type="submit"]');
      var restore = button ? button.innerHTML : null;
      if (button) { button.disabled = true; button.style.opacity = '0.7'; }

      function done() {
        if (button) { button.disabled = false; button.style.opacity = '1'; if (restore) button.innerHTML = restore; }
      }

      var payload = { id: variant, quantity: 1 };
      if (plan) payload.selling_plan = plan;

      var drawer = window.HydronateBlueCart;
      if (drawer && typeof drawer.add === 'function') {
        try {
          var maybe = drawer.add(payload);
          if (maybe && typeof maybe.then === 'function') maybe.then(done, done); else done();
        } catch (err) { done(); form.submit(); }
        return;
      }

      fetch(root.getAttribute('data-cart-url') || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload)
      }).then(function (res) {
        if (!res.ok) throw new Error('add failed');
        return res.json();
      }).then(function () {
        done();
        document.dispatchEvent(new CustomEvent('hn:cart:added', { detail: payload }));
        window.location.href = '/cart';
      }).catch(function () { done(); form.submit(); });
    });
  }

  /* ---------------------------------------------------------------- faq */
  function initFaq(root) {
    var items = qsa(root, '[data-hn-faq]');
    if (!items.length) return;

    function paint(openIdx) {
      each(items, function (item) {
        var on = item.getAttribute('data-hn-faq') === String(openIdx);
        item.setAttribute('data-on', on ? '1' : '0');
        item.style.borderColor = on ? COBALT : LINEN;
        item.style.background = on ? LINEN_BG : 'transparent';
        var q = qs(item, '[data-hn-faq-q]');
        if (q) q.style.color = on ? COBALT : INK;
        var sign = qs(item, '[data-hn-faq-sign]');
        if (sign) {
          sign.textContent = on ? '\u2013' : '+';
          sign.style.borderColor = on ? COBALT : LINEN;
          sign.style.color = on ? COBALT : INK;
        }
        var body = qs(item, '[data-hn-faq-body]');
        if (body) body.style.display = on ? 'block' : 'none';
        var head = qs(item, '[data-hn-faq-head]');
        if (head) head.setAttribute('aria-expanded', on ? 'true' : 'false');
      });
    }

    each(items, function (item) {
      var head = qs(item, '[data-hn-faq-head]');
      if (!head) return;
      head.setAttribute('role', 'button');
      head.setAttribute('tabindex', '0');
      function toggle() {
        paint(item.getAttribute('data-on') === '1' ? -1 : item.getAttribute('data-hn-faq'));
      }
      head.addEventListener('click', toggle);
      head.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); }
      });
    });
  }

  /* --------------------------------------------------------------- boot */
  var INITS = { nav: initNav, benefits: initBenefits, offer: initOffer, faq: initFaq };

  function boot(scope) {
    each((scope || document).querySelectorAll('[data-hn]'), function (root) {
      if (root.getAttribute('data-hn-ready') === '1') return;
      var fn = INITS[root.getAttribute('data-hn')];
      if (!fn) return;
      root.setAttribute('data-hn-ready', '1');
      fn(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(); });
  } else {
    boot();
  }

  // Theme editor re-renders a single section; re-init just that subtree.
  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
})();
